import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaHome,
  FaUserPlus,
  FaSignOutAlt,
  FaChevronLeft,
  FaChevronRight,
  FaBars,
  FaBuilding,
  FaUsers,
  FaCog,
  FaBox,
  FaSignature,
  FaFileAlt,
  FaHistory,
  FaTimes,
  FaSlidersH,
} from "react-icons/fa";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useAuth } from "../../hooks/useAuth";
import "./Navigation.css";

const Navigation = () => {
  const location = useLocation();
  const { user, isAuthenticated, loading: authLoading, logout } = useAuth();
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const isMobile = useIsMobile();

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Close mobile menu when route changes
  React.useEffect(() => {
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  }, [location.pathname, isMobile]);

  const userPermissions = user?.permissions || "childUser";
  const userEmail = user?.email || "";

  // Check permissions for different pages
  const canViewUsers = userPermissions === "admin";
  const canViewUnits =
    userPermissions === "admin" || userPermissions === "parentsUser";
  const canViewPersonnel =
    userPermissions === "admin" ||
    userPermissions === "parentsUser" ||
    userPermissions === "childUser";

  if (location.pathname === "/") {
    return null;
  }
  if (authLoading) {
    return null;
  }
  if (!isAuthenticated) {
    return null;
  }

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // still redirect
    }
    window.location.href = "/";
  };

  const menuItems = [
    {
      path: "/ראשי",
      label: "עמוד ראשי",
      icon: <FaHome />,
      isActive: location.pathname === "/ראשי",
    },
    // Show Signature menu for admin, parentsUser, and childUser
    ...(canViewPersonnel || canViewUnits
      ? [
          {
            path: "/signature",
            label: "חתימה/זיכוי",
            icon: <FaSignature />,
            isActive: location.pathname === "/signature",
          },
        ]
      : []),
    // Show Inventory menu for admin, parentsUser, and childUser
    ...(canViewPersonnel || canViewUnits
      ? [
          {
            path: "/inventory",
            label: "הפריטים שלי",
            icon: <FaBox />,
            isActive: location.pathname === "/inventory",
          },
          {
            path: "/history",
            label: "היסטוריה",
            icon: <FaHistory />,
            isActive: location.pathname === "/history",
          },
        ]
      : []),
    // Only show Items menu for admin and parentsUser
    ...(canViewUnits
      ? [
          {
            path: "/items",
            label: "הגדרות פריטים",
            icon: <FaCog />,
            isActive: location.pathname === "/items",
          },
        ]
      : []),
    // Only show Units menu for admin and parentsUser
    ...(canViewUnits
      ? [
          {
            path: "/units",
            label: "יחידות",
            icon: <FaBuilding />,
            isActive: location.pathname === "/units",
          },
        ]
      : []),
    // Only show Personnel menu for admin, parentsUser, and childUser
    ...(canViewPersonnel
      ? [
          {
            path: "/personnel",
            label: "חיילים",
            icon: <FaUsers />,
            isActive: location.pathname === "/personnel",
          },
        ]
      : []),
    // Only show Users menu for admin users
    ...(canViewUsers
      ? [
          {
            path: "/create-user",
            label: "משתמשים",
            icon: <FaUserPlus />,
            isActive: location.pathname === "/create-user",
          },
        ]
      : []),
    // Only show Employee Pages Management for admin users
    ...(canViewUsers
      ? [
          {
            path: "/employee-pages",
            label: "דפי חתימה",
            icon: <FaFileAlt />,
            isActive: location.pathname === "/employee-pages",
          },
        ]
      : []),
    // Logout button (always at the end)
    {
      path: "#",
      label: "התנתק",
      icon: <FaSignOutAlt />,
      isActive: false,
      isLogout: true,
    },
  ];

  // Only render mobile menu button on mobile
  const MobileMenuButton = () => {
    if (!isMobile) return null;
    return (
      <button
        className="mobile-menu-toggle"
        onClick={toggleMobileMenu}
        aria-label="תפריט"
        aria-expanded={isMobileMenuOpen}
      >
        <FaBars />
      </button>
    );
  };

  // Only render mobile overlay on mobile
  const MobileOverlay = () => {
    if (!isMobile) return null;
    return (
      <div
        className={`mobile-overlay ${isMobileMenuOpen ? "mobile-open" : ""}`}
        onClick={closeMobileMenu}
        aria-hidden="true"
      />
    );
  };

  return (
    <>
      <MobileMenuButton />
      <MobileOverlay />
      <nav
        className={`sidebar ${
          isCollapsed && !isMobile ? "sidebar-collapsed" : ""
        } ${isMobileMenuOpen ? "mobile-open" : ""} ${
          isMobile ? "mobile-sidebar" : ""
        }`}
      >
        {/* Sidebar Header */}
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <Link to="/" onClick={isMobile ? closeMobileMenu : undefined}>
              {isCollapsed && !isMobile ? (
                <div className="brand-icon">
                  <FaBars />
                </div>
              ) : (
                <h2>מערכת ניהול</h2>
              )}
            </Link>
          </div>
          {!isMobile && (
            <button
              className="sidebar-toggle"
              onClick={toggleSidebar}
              aria-label={isCollapsed ? "הרחב תפריט" : "צמצם תפריט"}
            >
              {isCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
            </button>
          )}
          {isMobile && (
            <button
              className="mobile-close-btn"
              onClick={closeMobileMenu}
              aria-label="סגור תפריט"
            >
              <FaTimes />
            </button>
          )}
        </div>

        {/* Navigation Menu */}
        <div className="sidebar-menu">
          <ul className="sidebar-list">
            {menuItems
              .filter((item) => !item.isLogout)
              .map((item) => (
                <li key={item.path} className="sidebar-item">
                  <Link
                    to={item.path}
                    className={`sidebar-link ${
                      item.isActive ? "sidebar-link-active" : ""
                    }`}
                    title={isCollapsed && !isMobile ? item.label : ""}
                    onClick={() => {
                      if (isMobile) closeMobileMenu();
                    }}
                  >
                    <span className="sidebar-icon">{item.icon}</span>
                    {!isCollapsed && (
                      <span className="sidebar-text">{item.label}</span>
                    )}
                  </Link>
                </li>
              ))}
            <li className="sidebar-item sidebar-spacer"></li>
            <li className="sidebar-item">
              <Link
                to="/settings"
                className={`sidebar-link ${
                  location.pathname === "/settings"
                    ? "sidebar-link-active"
                    : ""
                }`}
                title={isCollapsed && !isMobile ? "הגדרות" : ""}
                onClick={() => {
                  if (isMobile) closeMobileMenu();
                }}
              >
                <span className="sidebar-icon">
                  <FaSlidersH />
                </span>
                {!isCollapsed && (
                  <span className="sidebar-text">הגדרות</span>
                )}
              </Link>
            </li>
            {menuItems
              .filter((item) => item.isLogout)
              .map((item) => (
                <li key={item.path} className="sidebar-item">
                  <button
                    onClick={() => {
                      if (isMobile) closeMobileMenu();
                      handleLogout();
                    }}
                    className={`sidebar-link logout-link`}
                    title={isCollapsed && !isMobile ? item.label : ""}
                  >
                    <span className="sidebar-icon">{item.icon}</span>
                    {!isCollapsed && (
                      <span className="sidebar-text">{item.label}</span>
                    )}
                  </button>
                </li>
              ))}
          </ul>
        </div>
      </nav>
    </>
  );
};

export default Navigation;
